import { useId, useState } from 'react'
import { useTranslation } from 'react-i18next'
import useSWR from 'swr'
import { AlertTriangle, Check, Github, KeyRound, LayoutGrid, Loader2, Plug } from 'lucide-react'
import {
  Badge,
  Button,
  cn,
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Select,
} from '@ludus/ui'
import type {
  SourceDiscovery,
  SourceProjectDescriptor,
  SourceSettingField,
  SourceSettings,
  SourceSettingsForm,
} from '@ludus/core'
import {
  connectSource,
  discoverSource,
  fetchSourceSettingsForm,
  listSourceKinds,
  SourceApiError,
} from '@/lib/api/sources.ts'
import { OFF, SLOT_SPECS, toSlotMapping, type MappableSlot } from './slots.ts'

type Step = 'credential' | 'board' | 'slots'
type Picks = Record<MappableSlot, string>
type T = ReturnType<typeof useTranslation>['t']

const STEPS: readonly Step[] = ['credential', 'board', 'slots']
const NO_PICKS: Picks = { stageSource: OFF, groupKey: OFF, tags: OFF, numericValue: OFF }

interface ConnectSourceDialogProps {
  open: boolean
  onOpenChange: (next: boolean) => void
  pipelineId: string
  onConnected: () => void
}

export function ConnectSourceDialog({
  open,
  onOpenChange,
  pipelineId,
  onConnected,
}: ConnectSourceDialogProps) {
  const { t } = useTranslation()
  const formId = useId()

  const kindsSwr = useSWR(open ? '/sources' : null, listSourceKinds)
  const kinds = kindsSwr.data ?? []

  const [step, setStep] = useState<Step>('credential')
  const [kind, setKind] = useState('')
  const [scope, setScope] = useState('')
  const [credential, setCredential] = useState('')
  const [settings, setSettings] = useState<SourceSettings>({})
  const [discovery, setDiscovery] = useState<SourceDiscovery | null>(null)
  const [board, setBoard] = useState<SourceProjectDescriptor | null>(null)
  const [boardFilter, setBoardFilter] = useState('')
  const [picks, setPicks] = useState<Picks>(NO_PICKS)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<SourceApiError | null>(null)

  const effectiveKind = kind === '' ? (kinds[0] ?? '') : kind

  // The form is the step. Fetched per kind rather than bundled, because only the
  // adapter knows whether its source wants an organisation or a base URL — the
  // console holds no labels of its own for either (ADR-0004 §2).
  const formSwr = useSWR(
    open && effectiveKind !== '' ? ['/sources/settings', effectiveKind] : null,
    () => fetchSourceSettingsForm(effectiveKind),
  )
  const form: SourceSettingsForm | null = formSwr.data ?? null

  const setSetting = (key: string, value: string) =>
    setSettings((prev) => ({ ...prev, [key]: value }))

  // A required `extra` field blocks discovery for the same reason scope does:
  // the adapter said it cannot reach the source without it.
  const missingRequired = (form?.extra ?? []).some(
    (field) => field.required && (settings[field.key] ?? '') === '',
  )

  function close(next: boolean) {
    if (!next) {
      setStep('credential')
      setScope('')
      setCredential('')
      setSettings({})
      setDiscovery(null)
      setBoard(null)
      setBoardFilter('')
      setPicks(NO_PICKS)
      setError(null)
    }
    onOpenChange(next)
  }

  async function run(action: () => Promise<void>) {
    setBusy(true)
    setError(null)
    try {
      await action()
    } catch (err) {
      setError(
        err instanceof SourceApiError
          ? err
          : new SourceApiError({ kind: 'unknown', status: null, message: String(err) }),
      )
    } finally {
      setBusy(false)
    }
  }

  const onDiscover = () =>
    run(async () => {
      const found = await discoverSource(effectiveKind, { scope, credential, settings })
      setDiscovery(found)
      setStep('board')
    })

  const onConnect = () =>
    run(async () => {
      if (board === null) return
      try {
        await connectSource(pipelineId, {
          adapterKind: effectiveKind,
          externalRef: board.externalRef,
          slotMapping: toSlotMapping(picks),
          settings,
          credential,
        })
      } catch (err) {
        // The engine keeps a connection whose stages it could not read back, so this
        // one is saved: the page behind refreshes, and only the wizard reports.
        if (err instanceof SourceApiError && err.kind === 'stages_not_provisioned') onConnected()
        throw err
      }
      onConnected()
      close(false)
    })

  // A slot no field on this board could feed is a control whose only choice is "Off".
  const mappable = SLOT_SPECS.map((spec) => ({
    spec,
    options: (board?.fields ?? []).filter((f) => spec.accepts.includes(f.kind)),
  })).filter(({ options }) => options.length > 0)

  const saved = error?.kind === 'stages_not_provisioned'
  const blockedTransports = discovery?.transports.unavailable ?? []

  // A CRM account answers with dozens of boards; a filter keeps the list readable
  // without the console learning what any board is called.
  const projects = discovery?.projects ?? []
  const needle = boardFilter.trim().toLowerCase()
  const visibleProjects =
    needle === ''
      ? projects
      : projects.filter(
          (p) =>
            p.name.toLowerCase().includes(needle) || p.externalRef.toLowerCase().includes(needle),
        )

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <div>
            <DialogTitle>
              {t('pages.pipelines.connect.title', { defaultValue: 'Connect a source' })}
            </DialogTitle>
            <DialogDescription>
              {t('pages.pipelines.connect.description', {
                defaultValue:
                  'Mirror an external board into this pipeline. Ludus reads the board and keeps this pipeline in step with it.',
              })}
            </DialogDescription>
          </div>
          <DialogClose />
        </DialogHeader>

        <DialogBody className="py-5">
          <StepRail current={step} t={t} />

          <form id={formId} className="flex flex-col gap-5" onSubmit={(e) => e.preventDefault()}>
            {step === 'credential' && (
              <>
                <Fieldset label={t('pages.pipelines.connect.kind', { defaultValue: 'Source' })}>
                  {kinds.length === 0 ? (
                    <Notice
                      tone="muted"
                      text={t('pages.pipelines.connect.noKinds', {
                        defaultValue: 'No source adapter is configured for this deployment.',
                      })}
                    />
                  ) : (
                    <div className="grid grid-cols-2 gap-2">
                      {kinds.map((k) => (
                        <KindTile
                          key={k}
                          kind={k}
                          selected={k === effectiveKind}
                          onSelect={() => setKind(k)}
                        />
                      ))}
                    </div>
                  )}
                </Fieldset>

                {formSwr.isLoading && (
                  <Notice
                    tone="muted"
                    text={t('pages.pipelines.connect.loadingForm', {
                      defaultValue: 'Asking the source what it needs…',
                    })}
                  />
                )}
                {formSwr.error != null && (
                  <Notice
                    role="alert"
                    tone="danger"
                    text={t('pages.pipelines.connect.formUnreadable', {
                      defaultValue: 'The source could not say what it needs to connect.',
                    })}
                  />
                )}
                {form !== null && (
                  <>
                    <SettingInput field={form.scope} value={scope} onChange={setScope} autoFocus />
                    <SettingInput
                      field={form.credential}
                      value={credential}
                      onChange={setCredential}
                    />
                    {form.extra.map((field) => (
                      <SettingInput
                        key={field.key}
                        field={field}
                        value={settings[field.key] ?? ''}
                        onChange={(next) => setSetting(field.key, next)}
                      />
                    ))}
                  </>
                )}
                {busy && (
                  <Notice
                    tone="muted"
                    spinner
                    text={t('pages.pipelines.connect.discovering', {
                      kind: effectiveKind,
                      defaultValue:
                        'Asking {{kind}} for its boards — a large account takes a moment…',
                    })}
                  />
                )}
              </>
            )}

            {step === 'board' && discovery !== null && (
              <>
                {blockedTransports.map((blocked) => (
                  <Notice key={blocked.transport} tone="warning" text={blocked.reason} />
                ))}
                {discovery.projects.length === 0 ? (
                  <Notice
                    tone="muted"
                    text={t('pages.pipelines.connect.noBoards', {
                      defaultValue: 'This token cannot see any board here.',
                    })}
                  />
                ) : (
                  <>
                    {projects.length > 8 && (
                      <Input
                        value={boardFilter}
                        onChange={(e) => setBoardFilter(e.target.value)}
                        placeholder={t('pages.pipelines.connect.filterBoards', {
                          defaultValue: 'Filter boards by name or id…',
                        })}
                        aria-label={t('pages.pipelines.connect.filterBoards', {
                          defaultValue: 'Filter boards by name or id…',
                        })}
                        autoFocus
                      />
                    )}
                    <Fieldset
                      label={t('pages.pipelines.connect.boardsFound', {
                        count: projects.length,
                        shown: visibleProjects.length,
                        defaultValue: 'Board — {{shown}} of {{count}}',
                      })}
                    >
                      <div className="flex flex-col gap-2">
                        {visibleProjects.map((project) => (
                          <BoardCard
                            key={project.externalRef}
                            project={project}
                            selected={project.externalRef === board?.externalRef}
                            onSelect={() => setBoard(project)}
                            t={t}
                          />
                        ))}
                        {visibleProjects.length === 0 && (
                          <Notice
                            tone="muted"
                            text={t('pages.pipelines.connect.noBoardMatches', {
                              defaultValue: 'No board matches the filter.',
                            })}
                          />
                        )}
                      </div>
                    </Fieldset>
                  </>
                )}
              </>
            )}

            {step === 'slots' && board !== null && (
              <>
                {mappable.length === 0 && (
                  <Notice
                    tone="muted"
                    text={t('pages.pipelines.connect.noSlots', {
                      defaultValue:
                        'No field on this board fits any of the optional slots. The adapter reads the rest from each card.',
                    })}
                  />
                )}
                {mappable.map(({ spec, options }) => (
                  <Fieldset
                    key={spec.slot}
                    label={t(`pages.pipelines.connect.slots.${spec.slot}`, {
                      defaultValue: spec.slot,
                    })}
                    hint={t(`pages.pipelines.connect.slotHints.${spec.slot}`, { defaultValue: '' })}
                  >
                    <Select
                      value={picks[spec.slot]}
                      options={[
                        {
                          value: OFF,
                          label: t('pages.pipelines.connect.off', { defaultValue: 'Not mapped' }),
                        },
                        ...options.map((f) => ({ value: f.id, label: f.name })),
                      ]}
                      onValueChange={(v) => setPicks((prev) => ({ ...prev, [spec.slot]: v }))}
                      className="w-full"
                      triggerClassName="w-full"
                    />
                  </Fieldset>
                ))}
              </>
            )}

            {error !== null && (
              <Notice
                role="alert"
                tone={saved ? 'warning' : 'danger'}
                text={errorMessage(error, t)}
              />
            )}
          </form>
        </DialogBody>

        <DialogFooter>
          <Button type="button" variant="outline" size="sm" onClick={() => close(false)}>
            {saved
              ? t('pages.pipelines.connect.done', { defaultValue: 'Done' })
              : t('pages.pipelines.connect.cancel', { defaultValue: 'Cancel' })}
          </Button>
          {step === 'credential' && (
            <Button
              type="button"
              variant="primary"
              size="sm"
              disabled={
                busy ||
                form === null ||
                scope === '' ||
                credential === '' ||
                effectiveKind === '' ||
                missingRequired
              }
              onClick={onDiscover}
            >
              {busy && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" aria-hidden="true" />}
              {busy
                ? t('pages.pipelines.connect.finding', { defaultValue: 'Finding boards…' })
                : t('pages.pipelines.connect.discover', { defaultValue: 'Find boards' })}
            </Button>
          )}
          {step === 'board' && (
            <Button
              type="button"
              variant="primary"
              size="sm"
              disabled={board === null}
              onClick={() => setStep('slots')}
            >
              {t('pages.pipelines.connect.mapFields', { defaultValue: 'Map fields' })}
            </Button>
          )}
          {step === 'slots' && !saved && (
            <Button type="button" variant="primary" size="sm" disabled={busy} onClick={onConnect}>
              {busy && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" aria-hidden="true" />}
              {t('pages.pipelines.connect.connect', { defaultValue: 'Connect' })}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function StepRail({ current, t }: { current: Step; t: T }) {
  const at = STEPS.indexOf(current)
  return (
    <ol className="mb-5 flex items-center gap-2">
      {STEPS.map((s, i) => {
        const done = i < at
        const here = i === at
        return (
          <li key={s} className="flex flex-1 items-center gap-2">
            <span
              className={cn(
                'flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px] font-semibold',
                done && 'bg-primary text-white',
                here && 'bg-primary-light text-primary ring-1 ring-primary/40',
                !done && !here && 'bg-background-secondary text-muted ring-1 ring-border',
              )}
            >
              {done ? <Check className="h-3 w-3" /> : i + 1}
            </span>
            <span
              className={cn(
                'text-[11px] font-medium whitespace-nowrap',
                here ? 'text-foreground' : 'text-muted',
              )}
            >
              {t(`pages.pipelines.connect.steps.${s}`, { defaultValue: s })}
            </span>
            {i < STEPS.length - 1 && <span className="h-px flex-1 bg-border" />}
          </li>
        )
      })}
    </ol>
  )
}

/**
 * One field, exactly as the adapter described it. The label, the hint and the
 * placeholder are printed unread — this component never matches on `key`, which
 * is what keeps a new source from needing a change here.
 *
 * `secret` is the one kind that changes behaviour rather than presentation: it
 * masks, disables autofill, and is the field the engine stores where nothing
 * reads it back.
 */
function SettingInput({
  field,
  value,
  onChange,
  autoFocus,
}: {
  field: SourceSettingField
  value: string
  onChange: (next: string) => void
  autoFocus?: boolean
}) {
  const secret = field.kind === 'secret'
  return (
    <Fieldset
      label={field.label}
      icon={secret ? KeyRound : undefined}
      hint={field.hint}
      optional={!field.required}
    >
      <Input
        type={secret ? 'password' : field.kind === 'url' ? 'url' : 'text'}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={field.placeholder}
        autoComplete={secret ? 'off' : undefined}
        autoFocus={autoFocus}
      />
    </Fieldset>
  )
}

function KindTile({
  kind,
  selected,
  onSelect,
}: {
  kind: string
  selected: boolean
  onSelect: () => void
}) {
  const Icon = kind === 'github' ? Github : Plug
  return (
    <button
      type="button"
      aria-pressed={selected}
      onClick={onSelect}
      className={cn(
        'flex items-center gap-2.5 rounded-lg border p-3 text-left transition-colors',
        selected
          ? 'border-primary/50 bg-primary-light'
          : 'border-border bg-background-secondary hover:border-border/80',
      )}
    >
      <Icon className={cn('h-4 w-4 shrink-0', selected ? 'text-primary' : 'text-muted')} />
      <span
        className={cn(
          'text-xs font-medium capitalize',
          selected ? 'text-primary' : 'text-foreground',
        )}
      >
        {kind}
      </span>
    </button>
  )
}

function BoardCard({
  project,
  selected,
  onSelect,
  t,
}: {
  project: SourceProjectDescriptor
  selected: boolean
  onSelect: () => void
  t: T
}) {
  return (
    <button
      type="button"
      aria-pressed={selected}
      onClick={onSelect}
      className={cn(
        'flex items-center gap-3 rounded-lg border p-3 text-left transition-colors',
        selected
          ? 'border-primary/50 bg-primary-light'
          : 'border-border bg-background-secondary hover:border-border/80',
      )}
    >
      <LayoutGrid
        className={cn('h-4 w-4 shrink-0', selected ? 'text-primary' : 'text-muted')}
        aria-hidden="true"
      />
      <span className="flex min-w-0 flex-col gap-0.5">
        <span className="truncate text-xs font-medium text-foreground">{project.name}</span>
        <span className="truncate font-mono text-[11px] text-muted">{project.externalRef}</span>
      </span>
      <Badge tone={selected ? 'primary' : 'muted'} size="sm" className="ml-auto shrink-0">
        {t('pages.pipelines.connect.fieldCount', {
          count: project.fields.length,
          defaultValue: '{{count}} fields',
        })}
      </Badge>
      {selected && <Check className="h-4 w-4 shrink-0 text-primary" aria-hidden="true" />}
    </button>
  )
}

function Fieldset({
  label,
  hint,
  icon: Icon,
  optional,
  children,
}: {
  label: string
  hint?: string
  icon?: typeof KeyRound
  optional?: boolean
  children: React.ReactNode
}) {
  const { t } = useTranslation()
  return (
    <label className="flex flex-col gap-1.5">
      <span className="flex items-center gap-1.5 text-[11px] font-semibold tracking-wide text-foreground uppercase">
        {Icon && <Icon className="h-3 w-3 text-muted" aria-hidden="true" />}
        {label}
        {optional === true && (
          <span className="font-normal normal-case text-muted">
            {t('pages.pipelines.connect.optional', { defaultValue: '(optional)' })}
          </span>
        )}
      </span>
      {children}
      {hint !== undefined && hint !== '' && <span className="text-[11px] text-muted">{hint}</span>}
    </label>
  )
}

function Notice({
  tone,
  text,
  role,
  spinner,
}: {
  tone: 'muted' | 'warning' | 'danger'
  text: string
  role?: string
  /** A wait, not a warning: the icon turns. */
  spinner?: boolean
}) {
  return (
    <p
      role={role}
      className={cn(
        'flex items-start gap-2 rounded-lg border p-2.5 text-[11px] leading-relaxed',
        tone === 'muted' && 'border-border bg-background-secondary text-muted',
        tone === 'warning' &&
          'border-toast-warning-text/20 bg-toast-warning text-toast-warning-text',
        tone === 'danger' && 'border-toast-error-text/20 bg-toast-error text-toast-error-text',
      )}
    >
      {spinner ? (
        <Loader2 className="mt-px h-3.5 w-3.5 shrink-0 animate-spin" aria-hidden="true" />
      ) : (
        <AlertTriangle className="mt-px h-3.5 w-3.5 shrink-0" aria-hidden="true" />
      )}
      <span>{text}</span>
    </p>
  )
}

function errorMessage(error: SourceApiError, t: T): string {
  switch (error.kind) {
    case 'source_in_use':
      return t('pages.pipelines.connect.errors.inUse', {
        defaultValue: 'Another pipeline already mirrors that board.',
      })
    case 'unmirrored_leads':
      return t('pages.pipelines.connect.errors.unmirroredLeads', {
        count: error.count ?? 0,
        defaultValue:
          'This pipeline already holds {{count}} cards of its own that no source will claim. Archive it and connect the source to a fresh pipeline.',
      })
    case 'invalid_input':
      return (
        error.detail ??
        t('pages.pipelines.connect.errors.invalid', { defaultValue: 'That is not a valid choice.' })
      )
    case 'source_unavailable':
      return (
        error.detail ??
        t('pages.pipelines.connect.errors.unavailable', {
          defaultValue: 'The source could not be reached.',
        })
      )
    case 'stages_not_provisioned':
      return t('pages.pipelines.connect.errors.notProvisioned', {
        defaultValue:
          'Connected. The stages could not be read yet and will appear after the next sync.',
      })
    case 'network':
      return t('pages.pipelines.connect.errors.network', {
        defaultValue: 'The engine could not be reached.',
      })
    default:
      return t('pages.pipelines.connect.errors.unknown', { defaultValue: 'Something went wrong.' })
  }
}
